/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { map } from 'lit/directives/map.js';
import { ViewBase } from '../view-base.js';
import { projectIndexViewStyles } from './project-index-view-styles.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/relative-time/relative-time.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js'
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js'
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js'
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js'

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-project-card/q1-project-card.js';

/*=== LIBRARY MODULES ===*/
import filter from 'lodash/filter';
import orderBy from 'lodash/orderBy';
const _ = { filter, orderBy };

/*=== Q1 MODULES ===*/
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';
import { enrichProject } from '../../modules/project-helpers-client.js';

/*=== CONSTANTS ===*/

/** The size of the window used to force a server-side cache refresh after multiple reloads.  */
const RECENT_RELOAD_WINDOW = 10; // in seconds

const PROJECT_INDEX_CACHES = {
  public: 'all-projects-public-index',
  network: 'all-projects-network-index',
  contributor: 'all-projects-network-index',
  foundation: 'all-projects-index',
  steward: 'all-projects-index',
};

const PROJECT_SEARCH_CACHE = 'all-projects-search';

const CACHE_MAX_AGE = 30; // in minutes

const SORT_FIELD_OPTIONS = [
  { label: 'Name', value: 'name' },
  { label: 'Created', value: 'createdAt' },
  { label: 'Members', value: 'memberCount' }
];
const SORT_ORDER_OPTIONS = [
  { icon: 'arrow_upward', label: 'Ascending', value: 'asc' },
  { icon: 'arrow_downward', label: 'Descending', value: 'desc' }
];

/*=== VIEW CLASS ===*/

export class ProjectIndexView extends FormHostMixin(ViewBase) {
  static styles = [
    ...super.styles,
    projectIndexViewStyles
  ];

  static properties = {
    ...super.properties,

    projects: { type: Array },
    projectsRefreshedAt: { type: Object },

    initComplete: { type: Boolean },
    refreshing: { type: Boolean },
    error: { type: Boolean },
    errorMessage: { type: String },

    sortField: { type: String },
    sortOrder: { type: String },
  };

  constructor() {
    super();
    this.projects = [];
    this.projectsRefreshedAt = null;

    this.initComplete = false;
    this.refreshing = true;
    this.error = false;
    this.errorMessage = null;

    this.sortField = 'name';
    this.sortOrder = 'asc';
  }
  
  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    this.setViewMeta();
    
    // If the viewer is reloading the page multiple times they probably want the latest data...
    const recentReloadCount = this._trackRecentReloadCount();
    this.refreshProjects(recentReloadCount >= 2);
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('sortField') || changedProperties.has('sortOrder')) {
      if (this.sortField && this.sortOrder) this.sortProjects();
    }
  }

  /**
   * Refreshes the projects list. If `forceLocalCacheRefresh` is true, the local cache will be
   * refreshed regardless of the cache's staleness. If false, the local cache will only be
   * refreshed if the cache is stale.
   * 
   * Note: The purpose of not always refreshing is to avoid unnecessary server calls since this 
   * view may receive lots of transient traffic in a single session.
   * 
   * @param {Boolean} forceLocalCacheRefresh - Defaults to true. If this is any truthy value
   * (such as the event from the refresh button) then it will trigger a server-side refresh.
   */
  async refreshProjects(forceLocalCacheRefresh = true) {
    this.refreshing = true;
    
    const projectIndexCacheId = PROJECT_INDEX_CACHES[this.appPersona];
    let projectIndexCache, projectSearchCache;

    try {
      projectIndexCache = !!forceLocalCacheRefresh
        ? await this.appElement.cacheManager.refreshLocalCache(projectIndexCacheId)
        : await this.appElement.cacheManager.getCache(projectIndexCacheId, CACHE_MAX_AGE);
      
      // Only accessible/relevant to signed in members
      if (this.isSignedIn) projectSearchCache = !!forceLocalCacheRefresh
        ? await this.appElement.cacheManager.refreshLocalCache(PROJECT_SEARCH_CACHE)
        : await this.appElement.cacheManager.getCache(PROJECT_SEARCH_CACHE, CACHE_MAX_AGE);
      
      this._processProjectsCaches(projectIndexCache, projectSearchCache);
      this.initComplete = true;

      if (projectIndexCache.isNewOrStale || projectSearchCache?.isNewOrStale) {
        if (projectIndexCache.isNewOrStale)
          projectIndexCache = await projectIndexCache.refreshComplete;
        if (projectSearchCache?.isNewOrStale)
          projectSearchCache = await projectSearchCache.refreshComplete;
        this._processProjectsCaches(projectIndexCache, projectSearchCache);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      this.error = true;
      this.errorMessage = error.message || 'Failed to load projects';
    } finally {
      this.initComplete = true;
      this.refreshing = false;
    }
  }

  /*=== ACTIONS ===*/

  /**
   * Overwrites `this.projects` with a sorted version of the passed projectsArray. If no array is
   * passed, the current `this.projects` array is sorted.
   */
  sortProjects(projectsArray = this.projects) {
    const sortedProjects = _.orderBy(projectsArray, [this.sortField], [this.sortOrder]);
    this.projects = sortedProjects;
  }

  showCreateProjectExplainerDialog() {
    this.renderRoot.getElementById('create-project-explainer').show();
  }
  
  hideCreateProjectExplainerDialog() {
    this.renderRoot.getElementById('create-project-explainer').hide();
  }

  /*=== GETTERS ===*/

  get canCreateProjects() {
    return this.isSignedIn 
      && ['contributor', 'foundation', 'steward'].includes(this.currentMember?.level);
  }

  get headerPrefix() {
    switch (this.appPersona) {
      case 'network':
      case 'contributor':
        return 'Member View';

      case 'foundation':
      case 'steward':
        return 'Foundation View';

      default:
        return 'Public View';
    }
  }

  get appHeaderSelected() {
    return 'projects';
  }

  /*=== TEMPLATES ===*/

  get contentTemplate() {
    return html`
      <header-bar class="with-border">
        <h1>
          <span class="prefix">${this.headerPrefix}</span>
          q1 Network Projects
        </h1>

        <sl-icon library="material" name="business_center" slot="icon" class="header-icon">
        </sl-icon>

        <button-bar>
          ${this.headerButtonBarTemplate}
        </button-bar>
      </header-bar>

      ${this.errorPanelTemplate}

      <div class="list-header-bar">
        ${this.listHeaderBarTemplate}
      </div>

      <div id="projects-list">
        ${this.projectsListTemplate}
      </div>

      <div class="list-footer-bar">
        <span class="query-count">
          ${this.projects?.length || 0} 
          ${this.projects?.length === 1 ? 'project is' : 'projects are'} 
          visible to you.
        </span>
      </div>

      ${this.createProjectExplainerDialogTemplate}      
    `;
  }

  get headerButtonBarTemplate() {
    return html`
      ${this.canCreateProjects ? html`
        <sl-button 
          size="large"
          variant="neutral" 
          href="/projects/create"
          pill
        >
          <sl-icon slot="prefix" library="material" name="add"></sl-icon>
          Create New Project
        </sl-button>
      ` : html`
        <sl-button 
          size="large"
          variant="neutral" 
          @click=${this.showCreateProjectExplainerDialog}
          pill
        >
          <sl-icon slot="prefix" library="material" name="add"></sl-icon>
          Create New Project
        </sl-button>
      `}
    `;
  }

  get listHeaderBarTemplate() {
    return html`
      <filter-bar>
        <span class="label hide-until-bp1">Sort:</span>

        <sl-radio-group
          name="sortField"
          size="small"
          @sl-change=${this.handleChange}
          value=${this.sortField}
        >
          ${map(SORT_FIELD_OPTIONS, option => html`
            <sl-tooltip content="Sort by ${option.label}">
              <sl-radio-button value=${option.value} pill>
                ${option.label}
              </sl-radio-button>
            </sl-tooltip>
          `)}
        </sl-radio-group>

        <sl-radio-group
          name="sortOrder"
          size="small"
          @sl-change=${this.handleChange}
          value=${this.sortOrder}
        >
          ${map(SORT_ORDER_OPTIONS, option => html`
            <sl-tooltip content="Sort ${option.label}">
              <sl-radio-button value=${option.value} pill>
                <sl-icon library="material" name=${option.icon} label=${option.label}></sl-icon>
              </sl-radio-button>
            </sl-tooltip>
          `)}
        </sl-radio-group>
      </filter-bar>
      <button-bar>
        ${!this.projectsRefreshedAt ? null : html`
          <span class="timestamp">
            Refreshed
            <sl-relative-time sync .date=${this.projectsRefreshedAt}></sl-relative-time>
          </span>
        `}

        <sl-button
          id="refresh-button"
          @click=${this.refreshProjects}
          ?disabled=${this.refreshing}
        >
          ${this.refreshing ? html`
            <sl-spinner slot="prefix"></sl-spinner>
            Refreshing
          ` : html`
            <sl-icon slot="prefix" library="material" name="refresh"></sl-icon>
            Refresh
          `}
        </sl-button>
      </button-bar>
    `;
  }

  get errorPanelTemplate() {
    if (!this.error) return null;

    return html`
      <sl-alert variant="danger" open>
        <sl-icon library="material" name="report" slot="icon"></sl-icon>

        <strong>An error occurred!</strong><br/>

        ${this.errorMessage}
      </sl-alert>
    `;
  }

  get projectsListTemplate() {
    return html`
      ${repeat(this.projects, g => g.id, project => html`
        <q1-project-card .project=${project}></q1-project-card>
      `)}

      ${this.projects.length || !this.initComplete ? null : html`
        <empty-alert>
          <sl-icon library="material" name="block"></sl-icon>
          There are no projects visible to you in the current view.
        </empty-alert>
      `}

      <q1-spinner-panel label="Loading..." ?open=${!this.initComplete}></q1-spinner-panel>
    `;
  }

  get createProjectExplainerDialogTemplate() {
    if (this.canCreateProjects) return null; // not relevant if you can create projects

    return html`
      <sl-dialog
        id="create-project-explainer"
        label="How to Create a New Project"
        @sl-after-hide=${this.hideCreateProjectDialog}
      >
        <div class="dialog-content">
          ${this.isSignedIn ? html`
            <p class="subtitle hero secondary">
              To create a new project, first you'll need to upgrade to the
              <strong>contributor</strong> level of membership.
            </p>

            <p>
              Becoming a contributor is fast, free, and non-restrictive of your other endeavors.
              Contributor membership lets you create projects & projects,
              contribute work & earn compensation, participate in Quorum1 governance, and more.
            </p>

            <p>
              Click the button below to learn more about the contributor level and how to upgrade.
            </p>
          ` : html`
            <p class="subtitle hero secondary">
              To create a new project, first you'll need to join the q1 Network as a member!
            </p>

            <p>
              The q1 Network is a high-trust community of work. A safe space for all forms of 
              professional collaboration. Membership is free.
            </p>

            <p>
              Click the button below to learn more about membership and how to join.
            </p>

            <p>
              <strong>Already a member?</strong>
              <a href="/signin">Click here to sign in.</a>
            </p>
          `}
        </div>

        <button-bar slot="footer">
          ${this.isSignedIn ? html`
            <sl-button href="/m/contributor-upgrade" variant="warning">
              Learn More
            </sl-button>
          ` : html`
            <sl-button href="/join" variant="warning">
              Learn More
            </sl-button>
          `}

          <sl-button @click=${this.hideCreateProjectExplainerDialog}>
            Close
          </sl-button>
        </button-bar>
      </sl-dialog>
    `;
  }

  /*=== UTILITY METHODS ===*/

  /**
   * Update the local `this.projects` array with the sorted projects from the index cache, along
   * with the active & pending project ids from the current member doc (if applicable).
   * Also updates the `this.projectsRefreshedAt` timestamp.
   * 
   * @param {object} projectIndexCache - The cache object containing the projects data.
   * @param {object} projectSearchCache - Optional. If included this will be used to enrich the 
   *   active & pending project ids from the current member doc and incorporate them into the list.
   */
  _processProjectsCaches(projectIndexCache, projectSearchCache) {
    // Start by initializing the unsorted project array and enriching it, while gathering the ids.
    const projectIds = new Set();
    const projects = Object.values(projectIndexCache.data?.projects || {});
    projects.forEach(project => {
      enrichProject(project); // enriches in place
      projectIds.add(project.id);
    });

    // If the viewer is signed in, enrich the active & pending project ids for current member
    if (this.isSignedIn && projectSearchCache && this.currentMember) {
      const projectSearchMap = buildRefPathMap(projectSearchCache.data, 'projects', 'projects');
      const { projectsActive, projectsPending } = this.currentMember;
      const cmProjectIds = [ ...(projectsActive || []), ...(projectsPending || []) ];

      cmProjectIds.filter(i => !projectIds.has(i)).forEach(projectId => {
        const project = projectSearchMap[`projects/${projectId}`];
        if (project) projects.push(enrichProject(project));
      });
    }

    // Finally, update the projects property with the sorted projects, and set refreshed at
    this.sortProjects(projects);
    this.projectsRefreshedAt = projectIndexCache.refreshedAt;
  }

  /**
   * Call this on connectedCallback in order to update the local storage tracker of how many 
   * times the viewer has RELOADED this page in the last RECENT_RELOAD_WINDOW seconds. 
   * 
   * This is used to force a server-side refresh if it seems like the viewer expects it.
   * 
   * @returns {number} The number of times the user has reloaded this page in the last
   * RECENT_RELOAD_WINDOW seconds.
   */
  _trackRecentReloadCount() {
    // Generate the current timestamp and calculate when our recent window started
    const currentTimestamp = Date.now();
    const windowStart = currentTimestamp - (RECENT_RELOAD_WINDOW * 1000);
    
    // Get the array of recent timestamps from local storage, add the current timestamp, and filter
    // out any timestamps that are older than the window start
    const cacheId = PROJECT_INDEX_CACHES[this.appPersona];
    let reloadTimestamps = [
      ...(JSON.parse(localStorage.getItem(`reloadTimestamps:${cacheId}`)) || []),
      currentTimestamp,
    ].filter(ts => ts > windowStart);

    // Save the updated array back to local storage
    localStorage.setItem(`reloadTimestamps:${cacheId}`, JSON.stringify(reloadTimestamps));

    return reloadTimestamps.length;
  }
}

customElements.define('project-index-view', ProjectIndexView);
